import { useRef } from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import style from "./style";
import Chip from "@mui/material/Chip";
import JSONInput from "react-json-editor-ajrm";
import locale from "react-json-editor-ajrm/locale/en";
import {
  globalStyles,
  noop,
  SUPPORTED_COUNTRIES,
} from "@client/shared/constants";
import { SnippetField, Dropdown } from "@client/shared/components";
import InputAdornment from "@mui/material/InputAdornment";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TeamSelector from "../team-selector";
import DeliveryFlow from "../delivery-flow";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Skeleton from "@mui/material/Skeleton";
import RingoutPrerecorder from "../ringout-prerecorder";

const NOTIFICATION_TRIGGER_TYPES = {
  PHONE_NUMBER: "phone-number",
  EXTENSION: "extension",
  VOICEMAIL_NOTIFICATION: "voicemail-notification",
};

const options = [
  {
    label: "Site Name",
    value: "{{siteName}}",
  },
  {
    label: "Timestamp",
    value: "{{timestamp}}",
  },
  {
    label: "Emergency Address",
    value: "{{emergencyAddress}}",
  },
  {
    label: "Extension Name",
    value: "{{extensionName}}",
  },
  {
    label: "Extension Number",
    value: "{{extensionNumber}}",
  },
];

const NotificationFormView = ({
  showAll = false,
  currentSection = 0,
  name = "",
  triggerNotificationType = NOTIFICATION_TRIGGER_TYPES.PHONE_NUMBER,
  isParsingPhoneNumber = false,
  externalNumber = "",
  voicemail_extensions = "",
  onPhoneNumParsedSuccess = noop,
  externalNumberError = null,
  parsePhoneNumber = noop,
  extensionNumber = "",
  callDirection = "Outbound",
  switches = {},
  triggers = {},
  sites = [],
  users = [],
  teams = [],
  delivery = {},
  handleInsertSnippet = noop,
  openWebhookDialog = false,
  showCodeInputs = false,
  renderWebhookHeaders = noop,
  handleWebhookEdit = noop,
  handleNotificationChange = noop,
  handleTrigerNotification = noop,
  handlePhoneNumber = noop,
  handleExtNumber = noop,
  handleNotificationDirection = noop,
  handleAffectNotification = noop,
  handleExtPattern = noop,
  handleSiteExt = noop,
  handleAllUsers = noop,
  handleUsers = noop,
  handleExtension = noop,
  handleChange = noop,
  handleChecked = noop,
  handleSnippet = noop,
  handleEmailChange = noop,
  handleWebhook = noop,
  handleSms = noop,
  handleSmsFrom = noop,
  handleRingoutChange = noop,
  emailSwitch = noop,
  sendImmediateSwitch = noop,
  webhookSwitch = noop,
  glipSwitch = noop,
  smsSwitch = noop,
  ringoutSwitch = noop,
  handleTeamChange = noop,
  handleOpenWebhook = noop,
  cancelWEbhookEdit = noop,
  addHeader = noop,
  selectedCountry = {},
  countryList = [],
  handleCountrySelection = noop,
  handleParsePhoneNumber = noop,
  handleWebhooksChange = noop,
  handleSelectionOption = noop,
  handleJsonEditer = noop,
  handleKeyDown = noop,
  timezones = [],
  handlePrefferedTimezone = noop,
  handleVoicemailExtension = noop,
  extensionList = [],
  error = {},
  deliverySteps = [],
  onDeliveryStepsUpdate = noop,
  notification = {},
  onDeliveryFlowReady = noop,
  jsonEditorRef = null,
  loadedExtension = false,
  onNewTeamCreated = noop,
  smsFromOptions = [],
  smsFromOptionsLoading = false,
}) => {
  return (
    <>
      <Box
        sx={{
          ...style.formWrapper,
          paddingBottom: "8px",
          paddingLeft: 4,
          marginTop: "16px",
        }}
      >
        {(showAll || currentSection === 0) && (
          <Box
            sx={
              showAll
                ? { ...style.formSectionWrapper, marginTop: 6 }
                : { ...style.formSectionWrapper }
            }
          >
            <Typography sx={{ color: "gray", marginBottom: 1 }} variant="h5">
              Notification Name
            </Typography>
            <TextField
              size="small"
              sx={{ width: "332px" }}
              label="Name your notification"
              name="notification_name"
              onChange={(event, error) => {
                handleNotificationChange(event, error);
              }}
              placeholder="Name your notification"
              error={!!error?.notification_name}
              helperText={error?.notification_name}
              value={name}
            />
          </Box>
        )}
        {(showAll || currentSection === 1) && (
          <Box
            sx={
              showAll
                ? { ...style.formSectionWrapper, marginTop: 6 }
                : { ...style.formSectionWrapper }
            }
          >
            <Typography sx={{ color: "gray", marginBottom: 1 }} variant="h5">
              Number or Extension to Trigger Notification
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ color: "grey", fontFamily: "serif" }}
            >
              {triggerNotificationType === "voicemail-notification"
                ? "This is the voicemail box that will trigger the notification."
                : "This is the number or account extension that triggers the notification to be sent."}
            </Typography>
            <RadioGroup
              sx={{ display: "flex", flexDirection: "row" }}
              aria-label="Trigger Notification Type"
              name="trigger-notification-type"
              value={triggerNotificationType}
              onChange={(event) => {
                handleTrigerNotification(event);
                const isVoicemailExtensions =
                  (event.currentTarget
                    ? event.currentTarget.value
                    : event.target?.value) ===
                  NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION;
                handleAffectNotification({
                  currentTarget: { checked: !isVoicemailExtensions },
                });
              }}
            >
              <FormControlLabel
                value={NOTIFICATION_TRIGGER_TYPES.PHONE_NUMBER}
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ marginRight: 2 }} variant="body2">
                      Phone number
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={NOTIFICATION_TRIGGER_TYPES.EXTENSION}
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ marginRight: "4px" }} variant="body2">
                      Extension
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION}
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ marginRight: "4px" }} variant="body2">
                      Voicemail Notification
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
            {triggerNotificationType ===
              NOTIFICATION_TRIGGER_TYPES.PHONE_NUMBER && (
              // <TextField
              //   classes={{
              //     list: {
              //       paper: style.phoneList,
              //       listItem: style.phoneListItem,
              //     },
              //   }}
              //   disabled={isParsingPhoneNumber}
              //   value={externalNumber}
              //   options={countries}
              //   listPlacement="bottom-start"
              //   onChange={(evt, error) => {
              //     handlePhoneNumber(evt, error);
              //     onPhoneNumParsedSuccess(false);
              //   }}
              //   // validations={{
              //   //   [VALIDATIONS.REQUIRED]: true,
              //   //   [VALIDATIONS.MIN_LENGTH]: 3,
              //   // }}
              //   onBlur={(event) => {
              //     if (externalNumber.length < 3 || !!externalNumberError) {
              //       return;
              //     }
              //     //   setState((prevState) => ({
              //     //     ...prevState,
              //     //     isParsingPhoneNumber: true,
              //     //   }));
              //     parsePhoneNumber();
              //   }}
              // />
              <TextField
                fullWidth
                size="small"
                disabled={isParsingPhoneNumber}
                label="Phone #"
                name="main_number"
                value={externalNumber}
                sx={{ width: "350px", marginTop: "12px" }}
                error={!!error?.main_number}
                helperText={error?.main_number}
                onKeyDown={(event) => handleKeyDown(event)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          paddingRight: 1,
                          borderRight: "1px solid gray",
                          height: "24px",
                        }}
                      >
                        <Dropdown
                          disablePortal={false}
                          customToggle={
                            <Box
                              sx={{
                                ...globalStyles.display.flex,
                                ...globalStyles.flex.align.center,
                                ...globalStyles.cursor.pointer,
                                paddingTop: "2px",
                              }}
                            >
                              <Typography variant="body2" color="common.black">
                                {selectedCountry.isoCode}
                              </Typography>
                              <KeyboardArrowDown />
                            </Box>
                          }
                          classes={{ menu: { maxHeight: 320, maxWidth: 220 } }}
                          options={[
                            ...SUPPORTED_COUNTRIES,
                            ...countryList.filter(
                              (country) =>
                                SUPPORTED_COUNTRIES.findIndex(
                                  (supportedCountry) =>
                                    supportedCountry.isoCode ===
                                    country.isoCode,
                                ) === -1,
                            ),
                          ].map((list) => {
                            return {
                              label: `${list.name} (${list.isoCode}) +${list.callingCode}`,
                              onClick: () => handleCountrySelection(list),
                            };
                          })}
                        />
                      </Box>
                    </InputAdornment>
                  ),
                }}
                onBlur={(event) => {
                  if (externalNumber.length < 3 || !!externalNumberError) {
                    return;
                  }
                  handleParsePhoneNumber();
                  parsePhoneNumber();
                }}
                onChange={(evt, error) => {
                  handlePhoneNumber(evt, error);
                  onPhoneNumParsedSuccess(false);
                  handleVoicemailExtension(evt, null);
                }}
              />
            )}
            {triggerNotificationType ===
              NOTIFICATION_TRIGGER_TYPES.EXTENSION && (
              <>
                {!loadedExtension ? (
                  <Skeleton width="100%" height={92} />
                ) : (
                  <Autocomplete
                    label="Extension number"
                    // getOptionLabel={(option) => {
                    //   return `${option.extensionNumber || ""}${
                    //     option.extensionNumber ? "-" : ""
                    //   }${option.name || ""}`;
                    // }}
                    getOptionLabel={(option) => {
                      return `${option.name || ""}${option.extensionNumber ? ` (Ext. ${option.extensionNumber})` : ""}`;
                    }}
                    value={
                      (extensionNumber &&
                        (extensionList || []).find(
                          (ext) =>
                            ext.extensionNumber ==
                            (extensionNumber?.extensionNumber ||
                              extensionNumber),
                        )) ||
                      {}
                    }
                    options={extensionList}
                    onChange={(evt, value) => {
                      handleExtNumber(value);
                      handleVoicemailExtension(evt, null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        size="small"
                        placeholder="Type or select extension from the dropdown"
                        {...params}
                        label="Extension number"
                      />
                    )}
                  />
                )}
              </>
              // <TextField
              //   size="small"
              //   name="ext_number"
              //   sx={{ ...style.extensionInput, marginTop: "12px" }}
              //   label="Enter ext number"
              //   placeholder="12345678"
              //   error={!!error?.ext_number}
              //   helperText={error?.ext_number}
              //   onChange={(event, error) => {
              //     handleExtNumber(event, error, "number");
              //     handleVoicemailExtension(event, null);
              //   }}
              //   // validations={{
              //   //   [VALIDATIONS.REQUIRED]: true,
              //   //   [VALIDATIONS.MAX_LENGTH]: 8,
              //   // }}
              //   value={extensionNumber}
              // />
            )}
            {triggerNotificationType ===
              NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION && (
              <>
                {!loadedExtension ? (
                  <Skeleton width="100%" height={92} />
                ) : (
                  <Autocomplete
                    label="Voicemail notification"
                    getOptionLabel={(option) => {
                      return `${option.name || ""}${option.extensionNumber ? ` (Ext. ${option.extensionNumber})` : ""}`;
                    }}
                    value={
                      (voicemail_extensions &&
                        (extensionList || []).find(
                          (ext) =>
                            ext.extensionNumber ==
                            (voicemail_extensions?.extensionNumber ||
                              voicemail_extensions),
                        )) ||
                      {}
                    }
                    options={extensionList}
                    onChange={(event, value) => {
                      handleVoicemailExtension(event, value);
                      handleSiteExt({ currentTarget: { checked: false } });
                      handleExtPattern({ currentTarget: { checked: false } });
                      handleAffectNotification({
                        currentTarget: { checked: false },
                      });
                      handleNotificationDirection({
                        currentTarget: {
                          value: "Inbound",
                        },
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        size="small"
                        placeholder="Type or select user(s) from the dropdown"
                        {...params}
                        label="Extension of Mailbox"
                      />
                    )}
                  />
                )}
              </>
            )}
          </Box>
        )}
        {(showAll || currentSection === 2) && (
          <Box
            sx={
              showAll
                ? { ...style.formSectionWrapper, marginTop: 6 }
                : { ...style.formSectionWrapper }
            }
          >
            <Typography sx={{ color: "gray", marginBottom: 1 }} variant="h5">
              Call Direction for Notification Trigger
            </Typography>
            <Typography
              sx={{ color: "gray", fontFamily: "sans-serif" }}
              variant="subtitle2"
            >
              Which call direction should generate the notification?
            </Typography>
            {triggerNotificationType ===
              NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION && (
              <Alert sx={{ my: 2 }} severity="warning">
                Only inbound direction is allowed for voicemail notifications.
              </Alert>
            )}
            <RadioGroup
              sx={{ display: "flex", flexDirection: "row" }}
              aria-label="call direction"
              name="call_direction"
              value={callDirection}
              onChange={(event) => handleNotificationDirection(event)}
            >
              <FormControlLabel
                disabled={
                  triggerNotificationType ===
                  NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                }
                value="Inbound"
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <Typography className="mr-2" variant="body2">
                      Inbound
                    </Typography>
                    <img
                      alt=""
                      src={
                        callDirection === "Inbound" &&
                        triggerNotificationType !==
                          NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                          ? "/assets/inbound-call.svg"
                          : "/assets/inbound-call-grey.svg"
                      }
                    />
                  </Box>
                }
              />
              <FormControlLabel
                disabled={
                  triggerNotificationType ===
                  NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                }
                value="Outbound"
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <Typography sx={{ marginRight: "4px" }} variant="body2">
                      Outbound
                    </Typography>
                    <img
                      alt=""
                      src={
                        callDirection === "Outbound"
                          ? "/assets/outbound-call.svg"
                          : "/assets/outbound-call-grey.svg"
                      }
                    />
                  </Box>
                }
              />
              <FormControlLabel
                disabled={
                  triggerNotificationType ===
                  NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                }
                value="Both Directions"
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <Typography sx={{ marginRight: "4px" }} variant="body2">
                      Both Directions
                    </Typography>
                    <img
                      alt=""
                      src={
                        callDirection === "Both Directions"
                          ? "/assets/bothdirections-call.svg"
                          : "/assets/bothdirections-call-grey.svg"
                      }
                    />
                  </Box>
                }
              />
            </RadioGroup>
          </Box>
        )}
        {(showAll || currentSection === 3) &&
          triggerNotificationType !==
            NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION && (
            <Box
              sx={
                showAll
                  ? { ...style.formSectionWrapper, marginTop: 6 }
                  : { ...style.formSectionWrapper }
              }
            >
              <Typography sx={{ color: "grey", marginBottom: 1 }} variant="h5">
                What call objects are affected by this notification?
              </Typography>
              {triggerNotificationType ===
                NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION && (
                <Alert sx={{ my: 2 }} severity="warning">
                  This notification will be triggered when a voicemail is
                  received on {voicemail_extensions?.extensionNumber || "-"}
                </Alert>
              )}
              <Box sx={style.formWrapper}>
                <Box
                  sx={{
                    paddingLeft: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Switch
                      disabled={
                        triggerNotificationType ===
                        NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                      }
                      checked={switches.users}
                      color="secondary"
                      // label="Users"
                      labelProps={{
                        classes: {
                          label: "color-grey secondary-font pl-1",
                        },
                      }}
                      onChange={(event) => handleAffectNotification(event)}
                    />
                    <Typography
                      sx={
                        triggerNotificationType ===
                        NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                          ? { color: "grey.400" }
                          : {}
                      }
                      variant="subtitle2"
                    >
                      Users
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Switch
                      disabled={
                        triggerNotificationType ===
                        NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                      }
                      checked={switches.extensionPattern}
                      color="secondary"
                      // label="Extension Pattern"
                      labelProps={{
                        classes: {
                          root: "ml-6",
                          label: "color-grey secondary-font pl-1",
                        },
                      }}
                      onChange={(event) => handleExtPattern(event)}
                    />
                    <Typography
                      sx={
                        triggerNotificationType ===
                        NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                          ? { color: "grey.400" }
                          : {}
                      }
                      variant="subtitle2"
                    >
                      Extension Pattern
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Switch
                      disabled={
                        triggerNotificationType ===
                        NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                      }
                      checked={switches.siteExtensions}
                      color="secondary"
                      // label="Site Extensions"
                      labelProps={{
                        classes: {
                          root: "ml-6",
                          label: "color-grey secondary-font pl-1",
                        },
                      }}
                      onChange={(event) => handleSiteExt(event)}
                    />
                    <Typography
                      sx={
                        triggerNotificationType ===
                        NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
                          ? { color: "grey.400" }
                          : {}
                      }
                      variant="subtitle2"
                    >
                      Site Extensions
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ marginTop: "36px" }}>
                  {switches.users && (
                    <>
                      <Typography variant="subtitle2">
                        Users that are affected:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "12px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <Switch
                            checked={switches.allUsers}
                            color="secondary"
                            label="All Users"
                            labelProps={{
                              classes: {
                                root: "mb-1 mt-1 pl-2",
                                label: "color-grey secondary-font pl-1",
                              },
                            }}
                            onChange={(event) => handleAllUsers(event)}
                          />
                          <Typography variant="subtitle2">All Users</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ marginTop: "16px" }}>
                        <Autocomplete
                          size="small"
                          getOptionLabel={(option) => {
                            return option.name || "";
                          }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              placeholder="Type or select user(s) from the dropdown"
                              {...params}
                              label="Users"
                            />
                          )}
                          value={triggers.users}
                          options={users}
                          onChange={(event, values) => {
                            handleUsers(event, values);
                          }}
                          multiple
                        />
                      </Box>
                    </>
                  )}
                </Box>
                <Box sx={{ marginTop: "32px" }}>
                  {switches.extensionPattern && (
                    <>
                      <Typography
                        sx={{ marginBottom: "12px" }}
                        variant="subtitle2"
                      >
                        Extension(s) and patterns that are affected:
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        label="Extension pattern"
                        name="extension_pattern"
                        placeholder="6****, 6000-6999 or 6789"
                        onChange={(event, error) => {
                          handleExtension(event, error);
                        }}
                        error={!!error?.extension_pattern}
                        helperText={error?.extension_pattern}
                        //   validations={{
                        //     [VALIDATIONS.REQUIRED]: state.switches.extensionPattern,
                        //     [VALIDATIONS.MAX_LENGTH]: 300,
                        //   }}
                        value={triggers.extensionPattern}
                      />
                    </>
                  )}
                </Box>
                <Box sx={{ marginTop: "32px" }}>
                  {switches.siteExtensions && (
                    <>
                      <Typography
                        sx={{ marginBottom: "12px" }}
                        variant="subtitle2"
                      >
                        Extension(s) at the following sites are affected:
                      </Typography>
                      <Autocomplete
                        size="small"
                        // label="Sites"
                        getOptionLabel={(option) => {
                          return option.name || "";
                        }}
                        renderInput={(params) => (
                          <TextField
                            placeholder="Type or select site(s) from the dropdown"
                            {...params}
                            label="Sites"
                          />
                        )}
                        value={triggers.sites}
                        options={sites}
                        onChange={(event, values) => {
                          handleChange(event, values);
                        }}
                        multiple
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        {(showAll || currentSection === 4) && (
          <Box
            sx={
              showAll
                ? { ...style.formSectionWrapper, marginTop: 6 }
                : { ...style.formSectionWrapper }
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography sx={{ color: "grey", marginBottom: 1 }} variant="h5">
                How would you like to be notified?
              </Typography>
              <Box
                sx={{
                  ml: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Switch
                  checked={switches.sendImmediate}
                  color="secondary"
                  // label="Email"
                  labelProps={{
                    classes: {
                      label: "color-grey secondary-font pl-1",
                    },
                  }}
                  onChange={(event) => sendImmediateSwitch(event)}
                />
                <Typography variant="body1">
                  Send all notifications immediately
                </Typography>
              </Box>
            </Box>
            {!switches.sendImmediate && (
              <DeliveryFlow
                teams={teams}
                users={users}
                deliverySteps={deliverySteps}
                onDeliveryStepsUpdate={onDeliveryStepsUpdate}
                voicemailExtensions={voicemail_extensions}
                onWebhookOpen={(webhook) => handleOpenWebhook(true, webhook)}
                onDeliveryFlowReady={onDeliveryFlowReady}
                countryList={countryList}
              />
            )}
            {switches.sendImmediate && (
              <>
                <Box
                  sx={{
                    paddingLeft: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Switch
                      checked={switches.email}
                      color="secondary"
                      // label="Email"
                      labelProps={{
                        classes: {
                          label: "color-grey secondary-font pl-1",
                        },
                      }}
                      onChange={(event) => emailSwitch(event)}
                    />
                    <Typography variant="body1">Email</Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Switch
                      checked={switches.webhook}
                      color="secondary"
                      label="Webhook"
                      // labelProps={{
                      //   classes: {
                      //     root: "ml-6",
                      //     label: "color-grey secondary-font pl-1",
                      //   },
                      // }}
                      onChange={(event) => webhookSwitch(event)}
                    />
                    <Typography variant="body1">Webhook</Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Switch
                      checked={switches.ringCentralApp}
                      color="secondary"
                      label="Glip"
                      labelProps={{
                        classes: {
                          root: "ml-6",
                          label: "color-grey secondary-font pl-1",
                        },
                      }}
                      onChange={(event) => glipSwitch(event)}
                    />
                    <Typography variant="body1">Glip</Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Switch
                      checked={switches.sms}
                      color="secondary"
                      label="SMS"
                      labelProps={{
                        classes: {
                          root: "ml-6",
                          label: "color-grey secondary-font pl-1",
                        },
                      }}
                      onChange={(event) => smsSwitch(event)}
                    />
                    <Typography variant="body1">SMS</Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Switch
                      checked={switches.ringout}
                      color="secondary"
                      label="SMS"
                      labelProps={{
                        classes: {
                          root: "ml-6",
                          label: "color-grey secondary-font pl-1",
                        },
                      }}
                      onChange={(event) => ringoutSwitch(event)}
                    />
                    <Typography variant="body1">
                      RingOut with recorded message
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ marginTop: "36px" }}>
                  {switches.email && (
                    <>
                      <Typography
                        sx={{ marginBottom: "12px" }}
                        variant="subtitle2"
                      >
                        Send an email to these address(es):
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        label="Email(s)"
                        name="email"
                        placeholder="abc@example.org, adc2@example.org"
                        onChange={(event, error) => {
                          handleEmailChange(event, error);
                        }}
                        error={!!error?.email}
                        helperText={error?.email}
                        // validations={{
                        //   [VALIDATIONS.REQUIRED]: state.switches.email,
                        //   [VALIDATIONS.MAX_LENGTH]: 500,
                        // }}
                        value={delivery.email}
                      />
                    </>
                  )}
                  {switches.webhook && (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 6,
                          marginBottom: 1,
                        }}
                      >
                        <Typography variant="subtitle2">
                          Send call info to the following Webhook address(es) or
                          code snippet:
                        </Typography>
                        <Box sx={{ display: "flex" }}>
                          <Button
                            sx={{ marginLeft: 4 }}
                            color="primary"
                            startIcon={<img alt="" src={"/assets/plus.svg"} />}
                            onClick={handleOpenWebhook}
                            variant="outlined"
                          >
                            <Typography variant="button" noWrap>
                              Add Webhook
                            </Typography>
                          </Button>
                        </Box>
                      </Box>
                      {delivery.webhooks.map((item, index) => {
                        return (
                          <Box sx={{ my: 2, display: "flex" }}>
                            <TextField
                              size="small"
                              key={index}
                              fullWidth
                              disabled
                              name="webhook"
                              label="Webhook"
                              placeholder="http://www.example.org"
                              onChange={(event, error) => {
                                handleWebhook(event, error);
                              }}
                              error={!!error?.webhook}
                              helperText={error?.webhook}
                              //   validations={{
                              //     [VALIDATIONS.REQUIRED]: state.switches.webhook,
                              //     [VALIDATIONS.MAX_LENGTH]: 500,
                              //   }}
                              value={item.webhook}
                            />
                            <Dropdown
                              // className="d-flex f-align-center"
                              // customToggle={(props) => {
                              //   return (
                              //     <MoreVertIcon
                              //       color="primary"
                              //       className="d-flex c-pointer"
                              //       ref={props.anchorRef}
                              //       onClick={props.onClick}
                              //     />
                              //   );
                              // }}
                              // onChange={(selectedOption) => {
                              //   handleSelectionOption(selectedOption, item);
                              // }}
                              options={[
                                {
                                  label: (
                                    <Typography variant="body1">
                                      Edit
                                    </Typography>
                                  ),
                                  actionType: "edit",
                                  onClick: () =>
                                    handleSelectionOption("edit", item),
                                },
                                {
                                  label: (
                                    <Typography variant="body1">
                                      Remove
                                    </Typography>
                                  ),
                                  actionType: "delete",
                                  onClick: () =>
                                    handleSelectionOption("delete", item),
                                },
                              ]}
                              placement="bottom-end"
                            />
                          </Box>
                        );
                      })}
                    </>
                  )}
                  {switches.ringCentralApp && (
                    <TeamSelector
                      label="Send a Glip message to the following teams:"
                      teams={teams}
                      onChange={(ringCentralTeams) =>
                        handleTeamChange(ringCentralTeams)
                      }
                      value={delivery.ringCentralTeams}
                      users={users}
                      onNewTeamCreated={onNewTeamCreated}
                    />
                  )}
                  {switches.sms && (
                    <>
                      <Typography sx={{ marginTop: 6 }} variant="subtitle2">
                        Send a SMS to these phone number(s):
                      </Typography>
                      <TextField
                        size="small"
                        label="Phone number(s)"
                        name="sms"
                        onChange={(event, error) => {
                          handleSms(event, error);
                        }}
                        placeholder="303-333-3333, 303-444-4444"
                        error={!!error?.sms}
                        helperText={error?.sms}
                        // validations={{
                        //   [VALIDATIONS.REQUIRED]: state.switches.sms,
                        //   [VALIDATIONS.MAX_LENGTH]: 500,
                        // }}
                        value={delivery.sms}
                      />
                      <Typography
                        sx={{ marginTop: 4, marginBottom: 1 }}
                        variant="subtitle2"
                      >
                        Send SMS from:
                      </Typography>
                      <Select
                        size="small"
                        name="sms_from"
                        value={delivery.sms_from || ""}
                        onChange={handleSmsFrom}
                        displayEmpty
                        sx={{ minWidth: 200 }}
                      >
                        {smsFromOptionsLoading ? (
                          <MenuItem value="" disabled>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary" }}
                            >
                              Loading phone numbers...
                            </Typography>
                          </MenuItem>
                        ) : (
                          <MenuItem value="">
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary" }}
                            >
                              Select a phone number
                            </Typography>
                          </MenuItem>
                        )}
                        {!smsFromOptionsLoading &&
                          smsFromOptions?.map((option) => (
                            <MenuItem
                              key={option.id || option.phoneNumber}
                              value={option.phoneNumber}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2">
                                  {option.phoneNumber}
                                </Typography>
                                {option.isMainNumber && (
                                  <Chip
                                    label="Main"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                      </Select>
                    </>
                  )}
                  {switches.ringout && (
                    <Box sx={{ mt: 6 }}>
                      <RingoutPrerecorder
                        ringout={delivery.ringout}
                        countryList={countryList}
                        handleRingoutChange={handleRingoutChange}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}
            {/* <Divider className='mt-6' /> */}
            <Box>
              <Typography
                sx={{ marginTop: 4, marginBottom: 2 }}
                variant="subtitle2"
              >
                Message Content
              </Typography>
              <SnippetField
                value={delivery.message}
                options={options}
                onChange={(message) => {
                  handleSnippet(message);
                }}
                notification={notification}
              />
              {!!delivery?.message?.includes("{{timestamp}}") && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Typography sx={{ mr: 2, fontWeight: "medium" }}>
                    Preferred timezone for timestamp:
                  </Typography>
                  <Dropdown
                    customToggle={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mt: 1,
                          p: 1,
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor: "grey.800",
                          borderRadius: "4px",
                        }}
                      >
                        <Typography sx={{ mr: 1 }}>
                          {delivery?.selectedTimezone?.name ||
                            "Select Timezone"}
                        </Typography>
                        <ArrowDropDownIcon
                        // onClick={props.onClick}
                        // ref={props.anchorRef}
                        />
                      </Box>
                    }
                    options={timezones.map((timezone) => {
                      return {
                        ...timezone,
                        label: timezone.name,
                        onClick: () => handlePrefferedTimezone(timezone),
                      };
                    })}
                    placement="top-end"
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Dialog
          sx={{
            "& .MuiDialog-paper": {
              maxWidth: "800px",
              width: "700px",
            },
          }}
          open={openWebhookDialog}
          //   onClose={() => {
          //     setState((prevState) => ({
          //       ...prevState,
          //       openWebhookDialog: false,
          //       showCodeInputs: false,
          //       delivery: {
          //         ...prevState.delivery,
          //         webhook: defaultState.delivery.webhook,
          //         headers: defaultState.delivery.headers,
          //         json: defaultState.delivery.json,
          //         webhookId: defaultState.delivery.webhookId,
          //       },
          //     }));
          //   }}
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {delivery.webhookId ? "Edit" : "Add"} Webhook
              </Typography>
              <IconButton>
                <CloseIcon
                  sx={{ color: "grey.600" }}
                  fontSize="small"
                  onClick={() => {
                    cancelWEbhookEdit();
                  }}
                />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ maxWidth: "700px", width: "700px" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2">
                {delivery.webhookId ? "Edit" : "Add"} your webhook URL and then
                your code snippet and any headers needed below.
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ marginTop: 3, fontWeight: "bold", marginBottom: "8px" }}
            >
              Enter Webhook URL address
            </Typography>
            <TextField
              size="small"
              fullWidth
              label="Webhook"
              name="webhookUrl"
              placeholder="http://www.example.org"
              onChange={(event, error) => {
                handleWebhooksChange(event, error);
              }}
              error={!!error?.webhookUrl}
              helperText={error?.webhookUrl}
              //   validations={{
              //     [VALIDATIONS.REQUIRED]: state.switches.webhook,
              //     [VALIDATIONS.MAX_LENGTH]: 500,
              //   }}
              value={delivery.webhook}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginTop: 2,
              }}
            >
              <Switch
                checked={showCodeInputs}
                color="primary"
                // label={

                // }
                labelProps={{
                  classes: {
                    root: "mt-2",
                    label: "color-grey secondary-font pl-1",
                  },
                }}
                onClick={() => {}}
                onChange={(event) => {
                  handleChecked(event);
                }}
              />
              <Typography
                //   className={clsx("medium-font mt-1", style.showInputSwitch)}
                sx={{ marginTop: 1, ...style.showInputSwitch }}
                variant="subtitle2"
              >
                Show Code Input and Headers
              </Typography>
            </Box>
            {!!showCodeInputs && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    // justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 6,
                  }}
                >
                  <Typography variant="subtitle2">
                    Paste and/or type code snippet and pre-built snippets for RC
                    properties
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", ...style.box }}
                >
                  <div ref={jsonEditorRef}>
                    {" "}
                    <JSONInput
                      id="json-text-editor"
                      placeholder={delivery.json}
                      theme="light_mitsuketa_tribute"
                      locale={locale}
                      height="174px"
                      width="392px"
                      onChange={({ jsObject, error }) => {
                        handleJsonEditer(jsObject, error);
                      }}
                      style={{
                        contentBox: {
                          paddingRight: 32,
                        },
                      }}
                    />
                  </div>
                  <Box
                    // className={clsx(
                    //   "d-flex flex-column f-align-end",
                    //   style.options
                    // )}
                    sx={{
                      ...style.options,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "end",
                    }}
                  >
                    {options.map((option, optionIndex) => {
                      return (
                        <Chip
                          sx={{
                            marginBottom: 1,
                            fontFamily: "regular",
                            backgroundColor: "grey.400",
                            color: "white",
                          }}
                          deleteIcon={
                            <span style={style.chipIconWrapper}>
                              <img
                                alt=""
                                style={style.chipIcon}
                                src="/assets/plus-grey-dark.svg"
                              />
                            </span>
                          }
                          key={optionIndex}
                          label={option.label}
                          size="small"
                          onClick={() => handleInsertSnippet(option)}
                          onDelete={() => {}}
                        />
                      );
                    })}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 6,
                    marginBottom: "4px",
                  }}
                >
                  <Typography variant="subtitle2">
                    Send custom headers to Webhook address(es):
                  </Typography>
                </Box>
                {renderWebhookHeaders(delivery.headers)}

                <Button
                  color="primary"
                  onClick={() => {
                    addHeader();
                  }}
                  sx={{ marginTop: 2 }}
                  startIcon={<img alt="" src={"/assets/plus.svg"} />}
                  variant="outlined"
                >
                  <Typography variant="button" noWrap>
                    Add Header
                  </Typography>
                </Button>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ marginRight: "4px", color: "gray" }}
              variant="outlined"
              onClick={() => {
                cancelWEbhookEdit();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                delivery.isJsonError ||
                !(delivery.webhook || "").trim() ||
                !!error.webhookUrl
              }
              // color="primary"
              variant="contained"
              onClick={() => {
                handleWebhookEdit({
                  webhookId: delivery.webhookId,
                  webhook: delivery.webhook,
                  headers: delivery.headers,
                  json: delivery.json || {},
                });
              }}
            >
              {delivery.webhookId ? "Edit" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default NotificationFormView;
